import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { getAccessToken, getActiveCompanyId, buildQuery, unwrapList, getTransportError } from './apiUtils';
import { ChartAccount } from './ChartOfAccountsService';

export interface JournalLine {
  account: string;
  accountName: string;
  debit: number;
  credit: number;
}

// Id de la cuenta: código exacto, prefijo más largo o la primera que empiece por él
export const resolveAccountId = (code: string, accounts: ChartAccount[]): string | undefined => {
  const exact = accounts.find(a => a.code === code && a.id);
  if (exact) return exact.id;

  let best: ChartAccount | null = null;
  for (const a of accounts) {
    if (a.id && code.startsWith(a.code) && (!best || a.code.length > best.code.length)) best = a;
  }
  if (best) return best.id;

  const wider = accounts.find(a => a.id && a.code.startsWith(code));
  return wider ? wider.id : undefined;
};

// journal_entries_post admite un apunte por llamada: se encadenan con entry_id
export const postJournalEntry = async (
  concept: string,
  lines: JournalLine[],
  sourceModule: string,
  accounts: ChartAccount[],
  entryDate: string = new Date().toISOString().split('T')[0]
): Promise<string> => {
  const totalDebit = lines.reduce((acc, l) => acc + l.debit, 0);
  const totalCredit = lines.reduce((acc, l) => acc + l.credit, 0);
  if (Math.abs(totalDebit - totalCredit) > 0.01) {
    throw new Error(`El asiento no cuadra (Debe: ${totalDebit.toFixed(2)} €, Haber: ${totalCredit.toFixed(2)} €).`);
  }

  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  // Se lee antes del bucle: si cambia a mitad, el asiento no se parte entre dos sociedades
  const companyId = getActiveCompanyId();

  const missing = lines
    .filter(l => (l.debit > 0.005 || l.credit > 0.005) && !resolveAccountId(l.account, accounts))
    .map(l => l.account);
  if (missing.length > 0) {
    throw new Error(`La(s) cuenta(s) ${Array.from(new Set(missing)).join(', ')} no existen en el plan contable.`);
  }

  type Pending = { accountId: string; remaining: number };
  const debitQueue: Pending[] = lines
    .filter(l => l.debit > 0.005)
    .map(l => ({ accountId: resolveAccountId(l.account, accounts) as string, remaining: l.debit }));
  const creditQueue: Pending[] = lines
    .filter(l => l.credit > 0.005)
    .map(l => ({ accountId: resolveAccountId(l.account, accounts) as string, remaining: l.credit }));

  let entryId: string | null = null;
  while (debitQueue.length > 0 && creditQueue.length > 0) {
    const d = debitQueue[0];
    const c = creditQueue[0];
    const amount = Math.min(d.remaining, c.remaining);

    const params: Record<string, any> = {
      access_token:      token,
      account_debit_id:  d.accountId,
      account_credit_id: c.accountId,
      amount:            amount.toFixed(2)
    };
    if (entryId) {
      params.entry_id = entryId;
    } else {
      params.company_id = companyId;
      params.entry_date = entryDate;
      params.concept = concept.toUpperCase();
      params.source_module = sourceModule;
    }

    let res = await ObexRequestsHandlerP80.get(`/journal_entries_post${buildQuery(params)}`);
    if (res && res.data) res = res.data;
    if (res && res.result) res = res.result;
    const record = res ? res.response : null;
    if (!record || typeof record !== 'object' || Array.isArray(record) || !record.id) {
      throw new Error(typeof record === 'string' ? record : 'El servidor no confirmó el asiento contable.');
    }
    entryId = String(record.id);

    d.remaining -= amount;
    c.remaining -= amount;
    if (d.remaining <= 0.005) debitQueue.shift();
    if (c.remaining <= 0.005) creditQueue.shift();
  }

  return entryId as string;
};

// Nº de asientos del Libro Diario
export const countJournalEntries = async (): Promise<number> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  const res = await ObexRequestsHandlerP80.get(`/journal_entries_list${buildQuery({ access_token: token, company_id: getActiveCompanyId() })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
  return unwrapList(res).length;
};

// Id del próximo asiento (el Libro Diario numera con el id)
export const nextJournalEntryId = async (): Promise<number> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  const res = await ObexRequestsHandlerP80.get(`/journal_entries_list${buildQuery({ access_token: token, company_id: getActiveCompanyId() })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
  return unwrapList(res).reduce((max: number, e: any) => Math.max(max, parseInt(e.id, 10) || 0), 0) + 1;
};

export interface BankJournalEntry {
  id: string;
  date: string;
  concept: string;
  sourceModule: string;
  amount: number;
  counterparts: string[];
}

const unwrapDetail = (res: any) => {
  let out = res;
  if (out && out.data) out = out.data;
  if (out && out.result) out = out.result;
  return out ? out.response : null;
};

/**
 * Líneas de todos los asientos de la sociedad (journal_lines_list), agrupadas por
 * journal_entry_id: una petición en vez de un journal_entries_get por asiento.
 * Devuelve null si el endpoint no está disponible, para que el llamante use el
 * camino antiguo.
 */
export const listJournalLinesByEntry = async (period: { from?: string; to?: string } = {}): Promise<Map<string, any[]> | null> => {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const res = await ObexRequestsHandlerP80.get(`/journal_lines_list${buildQuery({
      access_token: token,
      company_id:   getActiveCompanyId(),
      date_from:    period.from,
      date_to:      period.to
    })}`);
    if (getTransportError(res)) return null;
    const rows = unwrapList(res);
    // Un error del backend llega como texto, no como array
    if (rows.length === 0 && typeof unwrapDetail(res) === 'string') return null;
    const byEntry = new Map<string, any[]>();
    rows.forEach((l: any) => {
      const key = String(l.journal_entry_id);
      const bucket = byEntry.get(key);
      if (bucket) bucket.push(l); else byEntry.set(key, [l]);
    });
    return byEntry;
  } catch (err) {
    console.warn('journal_lines_list no disponible, se piden las líneas asiento a asiento:', err);
    return null;
  }
};

// Asientos del periodo contra la 572, con su importe neto (positivo si entra dinero)
export const listBankJournalEntries = async (accounts: ChartAccount[], from: string, to: string): Promise<BankJournalEntry[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  const res = await ObexRequestsHandlerP80.get(`/journal_entries_list${buildQuery({ access_token: token, company_id: getActiveCompanyId() })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);

  const byId = new Map(accounts.filter(a => a.id).map(a => [a.id, a] as [string, ChartAccount]));
  const inPeriod = unwrapList(res).filter((e: any) => {
    const date = String(e.entry_date || '').slice(0, 10);
    return (!from || date >= from) && (!to || date <= to) && !['void', 'voided', 'cancelled'].includes(String(e.status));
  });

  const linesByEntry = await listJournalLinesByEntry({ from, to });

  const detailed = await Promise.all(inPeriod.map(async (e: any): Promise<BankJournalEntry | null> => {
    try {
      let lines: any[];
      if (linesByEntry) {
        lines = linesByEntry.get(String(e.id)) || [];
      } else {
        const detail = unwrapDetail(await ObexRequestsHandlerP80.get(`/journal_entries_get${buildQuery({ access_token: token, id: e.id })}`));
        lines = detail && Array.isArray(detail.lines) ? detail.lines : [];
      }
      let amount = 0;
      const counterparts: string[] = [];
      lines.forEach((l: any) => {
        const account = byId.get(String(l.account_id));
        const code = account ? account.code : '';
        if (code.startsWith('572')) amount += (parseFloat(l.debit) || 0) - (parseFloat(l.credit) || 0);
        else if (code && !counterparts.includes(code)) counterparts.push(code);
      });
      return {
        id:           String(e.id),
        date:         String(e.entry_date || '').slice(0, 10),
        concept:      e.concept || '',
        sourceModule: e.source_module || '',
        amount:       Math.round(amount * 100) / 100,
        counterparts
      };
    } catch (err) {
      console.error('Error loading journal entry lines', e.id, err);
      return null;
    }
  }));
  return detailed.filter((e): e is BankJournalEntry => e !== null && Math.abs(e.amount) >= 0.005);
};
